import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type LogoutReason =
  | 'session_expired'
  | 'user_initiated'
  | 'inactivity'
  | null

export interface AuthState {
  accessToken: string | null
  refreshToken: string | null
  tenantSlug: string | null
  empId: string | null
  /** Why we last cleared the session. LoginPage reads this to show a
   *  clear "Your session expired" notice instead of a blank login form,
   *  which leaves the user wondering why they were redirected here.
   *  'inactivity' is kept distinct from 'session_expired': the token may
   *  still have been valid: we signed them out for being away, and on a
   *  shared collections handset that difference is worth stating. */
  logoutReason: LogoutReason
  setSession: (s: {
    accessToken: string
    refreshToken: string | null
    tenantSlug: string
    empId: string
  }) => void
  clearLogoutReason: () => void
  refresh: () => Promise<string | null>
  logout: (reason?: LogoutReason) => void
}

/**
 * Read the `exp` (seconds since epoch) out of a JWT without verifying the
 * signature — this is only a client-side hint so the router can redirect to
 * /login BEFORE rendering an authenticated screen with a dead token. The
 * server remains the real authority (it rejects the token on the wire).
 * Returns null if the token is malformed or carries no exp.
 */
function jwtExpSeconds(token: string): number | null {
  const payloadPart = token.split('.')[1]
  if (!payloadPart) return null
  try {
    const base64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/')
    const payload = JSON.parse(atob(base64)) as { exp?: number }
    return typeof payload.exp === 'number' ? payload.exp : null
  } catch {
    return null
  }
}

/**
 * True when the access token is absent, unparseable, or within `skewSeconds`
 * of its expiry. The skew treats a token that's about to die as already
 * dead, so we never render a screen that's guaranteed to 401 mid-flight.
 * A malformed token (exp null) is treated as expired — fail closed.
 */
export function isAccessTokenExpired(token: string | null, skewSeconds = 30): boolean {
  if (!token) return true
  const exp = jwtExpSeconds(token)
  if (exp == null) return true
  return Date.now() >= (exp - skewSeconds) * 1000
}

/**
 * True when there is still a viable path to an authenticated request: either
 * the access token is live, or the refresh token is live (so the client can
 * silently mint a new access token). Route guards use this to decide whether
 * to admit a navigation — an expired access token alone is NOT a logout as
 * long as the refresh token can revive it.
 */
export function hasLiveSession(s: { accessToken: string | null; refreshToken: string | null }): boolean {
  return !isAccessTokenExpired(s.accessToken) || !isAccessTokenExpired(s.refreshToken)
}

/**
 * True for a totp-pending session: a live (unexpired) access token but no
 * refresh token. That combination only happens right after a login that
 * returned `totpPending: true` — every other session shape either has both
 * tokens (fully authenticated) or neither (logged out). Route guards use
 * this to send the user to /auth/totp-setup instead of the dashboard or
 * the login form.
 */
export function isTotpPending(s: { accessToken: string | null; refreshToken: string | null }): boolean {
  return !isAccessTokenExpired(s.accessToken) && s.refreshToken === null
}

// Base URL mirrors createApiClient in lib/api/client.ts. refresh() uses a
// bare fetch (not the shared axios instance) to avoid a circular import and,
// more importantly, to bypass the 401 interceptor — a refresh that itself
// 401s must NOT recurse back into the refresh path.
const API_BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:8080'

// Single-flight guard: many queries can 401 at once when the access token
// expires. They all await this one shared promise so we hit /v1/auth/refresh
// exactly once and every caller gets the same rotated token.
let inflightRefresh: Promise<string | null> | null = null

/**
 * Auth store. Persisted to localStorage so the session survives page reload.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      tenantSlug: null,
      empId: null,
      logoutReason: null,
      setSession: (s) =>
        set({
          accessToken: s.accessToken,
          refreshToken: s.refreshToken,
          tenantSlug: s.tenantSlug,
          empId: s.empId,
          logoutReason: null,
        }),
      clearLogoutReason: () => set({ logoutReason: null }),
      refresh: async () => {
        const rt = get().refreshToken
        if (!rt) return null
        // Coalesce concurrent refreshes into one network call.
        if (inflightRefresh) return inflightRefresh
        inflightRefresh = (async () => {
          try {
            const res = await fetch(`${API_BASE_URL}/v1/auth/refresh`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ refreshToken: rt }),
            })
            if (!res.ok) {
              // A 4xx (401 session_expired) means the refresh token was
              // refused: expired, revoked, or past midnight. The session is
              // truly over; clear it so LoginPage explains why. A 5xx, 408 or
              // 429 is the server having a bad moment (a deploy restarting
              // the API) — keep the session and let the next request retry.
              if (res.status < 500 && res.status !== 408 && res.status !== 429) {
                get().logout('session_expired')
              }
              return null
            }
            const data = (await res.json()) as {
              accessToken: string
              refreshToken: string
              tenantSlug: string
              empId: string
            }
            get().setSession({
              accessToken: data.accessToken,
              refreshToken: data.refreshToken,
              tenantSlug: data.tenantSlug,
              empId: data.empId,
            })
            return data.accessToken
          } catch {
            // Network blip — don't nuke the session on a transient failure;
            // the original request just rejects and the user can retry.
            return null
          } finally {
            inflightRefresh = null
          }
        })()
        return inflightRefresh
      },
      logout: (reason = 'user_initiated') => {
        set({
          accessToken: null,
          refreshToken: null,
          tenantSlug: null,
          empId: null,
          logoutReason: reason,
        })
      },
    }),
    {
      name: 'grav-auth',
    },
  ),
)
